// GPO Governance — Active Directory Group Policy Object oversight

import { pbAuthedFetch } from '../authHeaders';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { StalenessIndicator } from '../components/provenance/StalenessIndicator';
import { useTableControls, type Column } from '../hooks/useTableControls';
import { DataTable } from '../components/DataTable';

export function GPOPage() {
  const { data: gpoData, isLoading: gpoLoading, error: gpoError, refetch: refetchGpo } = useQuery({
    queryKey: ['gpo'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/gpo');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: orphansData, isLoading: orphansLoading } = useQuery({
    queryKey: ['gpo-orphans'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/gpo/orphans');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: driftData, isLoading: driftLoading } = useQuery({
    queryKey: ['gpo-stig-drift'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/gpo/stig-drift');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: hygieneData, isLoading: hygieneLoading } = useQuery({
    queryKey: ['gpo-group-hygiene'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/gpo/group-hygiene');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const gpos = gpoData?.gpos || [];
  const orphans = orphansData?.orphans || {};
  const drift = driftData?.rules || [];
  const hygiene = hygieneData?.groups || [];

  const orphanLinked = orphans.linked_nowhere || [];
  const orphanStale = orphans.unmodified_over_2y || [];
  const orphanZeroFilter = orphans.zero_principal_filters || [];

  const orphansCombined = useMemo(() => [
    ...orphanLinked.map((gpo: any) => ({ name: gpo.display_name || gpo.name, issue: 'Unlinked', detail: 'Not linked to any organizational unit' })),
    ...orphanStale.map((gpo: any) => ({ name: gpo.display_name || gpo.name, issue: 'Stale', detail: `Last modified ${gpo.modified_at ? new Date(gpo.modified_at).toLocaleDateString() : 'unknown'}` })),
    ...orphanZeroFilter.map((gpo: any) => ({ name: gpo.display_name || gpo.name, issue: 'Zero-principal', detail: 'Security filter resolves to no accounts or groups' })),
  ], [orphanLinked, orphanStale, orphanZeroFilter]);

  const isLoading = gpoLoading || orphansLoading || driftLoading || hygieneLoading;

  const gpoColumns: Column<any>[] = [
    { key: 'display_name', label: 'Name', accessor: (r: any) => r.display_name || r.name, render: (r: any) => <span className="td--name">{r.display_name || r.name}</span> },
    { key: 'linked_ous', label: 'Linked OUs', sortable: true, accessor: (r: any) => r.linked_ous?.length ?? 0, render: (r: any) => r.linked_ous?.length > 0 ? <div style={{ display: 'flex', gap: 'var(--sp-1)', flexWrap: 'wrap' }}>{r.linked_ous.map((ou: string, i: number) => (<span key={i} className="pill" style={{ padding: '2px 6px', fontSize: 'var(--text-xs)' }}><i style={{ background: 'var(--ok)' }}></i>{ou}</span>))}</div> : <span style={{ color: 'var(--ink-45)' }}>None</span> },
    { key: 'enabled', label: 'Enabled', accessor: (r: any) => r.enabled !== false ? 'true' : 'false', filterOptions: [{ value: 'true', label: 'Enabled' }, { value: 'false', label: 'Disabled' }], render: (r: any) => <span className={`pill ${r.enabled !== false ? 'pill--ok' : 'pill--risk'}`}><i></i>{r.enabled !== false ? 'Enabled' : 'Disabled'}</span> },
    { key: 'modified_at', label: 'Last Modified', dateField: true, render: (r: any) => r.modified_at ? <StalenessIndicator lastSeenAt={r.modified_at} /> : <span style={{ color: 'var(--ink-45)' }}>Unknown</span> },
    { key: 'settings_count', label: 'Settings', render: (r: any) => <span className="td--num">{r.settings_count ?? '—'}</span> },
    { key: 'stig_mapped_count', label: 'STIG-Mapped', render: (r: any) => <span className="td--num">{r.stig_mapped_count ?? '—'}</span> },
  ];
  const gpoCtrl = useTableControls(gpos, gpoColumns, { storageKey: 'gpo-inventory' });

  const orphanColumns: Column<any>[] = [
    { key: 'name', label: 'GPO', render: (r: any) => <span className="td--id">{r.name}</span> },
    { key: 'issue', label: 'Issue', render: (r: any) => <span className={`pill ${r.issue === 'Unlinked' ? 'pill--risk' : r.issue === 'Stale' ? 'pill--warn' : 'pill--risk'}`}><i></i>{r.issue}</span> },
    { key: 'detail', label: 'Detail', render: (r: any) => <span style={{ color: 'var(--ink-45)' }}>{r.detail}</span> },
  ];
  const orphanCtrl = useTableControls(orphansCombined, orphanColumns, { storageKey: 'gpo-orphans' });

  const driftColumns: Column<any>[] = [
    { key: 'rule_id', label: 'STIG Rule', accessor: (r: any) => `${r.rule_id} ${r.title}`, render: (r: any) => (<><div style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)' }}>{r.rule_id}</div><div style={{ fontSize: 'var(--text-sm)' }}>{r.title}</div></>) },
    { key: 'severity', label: 'Severity', render: (r: any) => <span className={`pill ${r.severity === 'high' ? 'pill--risk' : r.severity === 'medium' ? 'pill--warn' : 'pill--ok'}`}><i></i>{r.severity}</span> },
    { key: 'compliant', label: 'Compliant', render: (r: any) => <span className="td--num" style={{ color: 'var(--ok)' }}>{r.compliant ?? 0}</span> },
    { key: 'non_compliant', label: 'Non-Compliant', render: (r: any) => <span className="td--num" style={{ color: 'var(--risk)' }}>{r.non_compliant ?? 0}</span> },
    { key: 'not_configured', label: 'Not Configured', render: (r: any) => <span className="td--num" style={{ color: 'var(--gold)' }}>{r.not_configured ?? 0}</span> },
    { key: 'status', label: 'Status', render: (r: any) => <span className={`pill ${r.status === 'compliant' ? 'pill--ok' : r.status === 'non-compliant' ? 'pill--risk' : 'pill--warn'}`}><i></i>{r.status}</span> },
  ];
  const driftCtrl = useTableControls(drift, driftColumns, { storageKey: 'gpo-stig-drift' });

  const hygieneColumns: Column<any>[] = [
    { key: 'name', label: 'Group', render: (r: any) => <span className="td--id">{r.name}</span> },
    { key: 'issues', label: 'Issues', sortable: false, render: (r: any) => (<div style={{ display: 'flex', gap: 'var(--sp-1)', flexWrap: 'wrap' }}>{r.empty && <span className="pill pill--risk"><i></i>Empty</span>}{!r.managed_by && <span className="pill pill--warn"><i></i>No owner</span>}{r.only_disabled && <span className="pill pill--warn"><i></i>All disabled</span>}</div>) },
    { key: 'member_count', label: 'Members', render: (r: any) => <span className="td--num">{r.member_count ?? '—'}</span> },
    { key: 'managed_by', label: 'Managed By', render: (r: any) => <span className="td--id">{r.managed_by || '—'}</span> },
  ];
  const hygieneCtrl = useTableControls(hygiene, hygieneColumns, { storageKey: 'gpo-group-hygiene' });

  const nonCompliantCount = drift.filter((r: any) => r.status === 'non-compliant').length;

  if (isLoading) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Active Directory</p>
              <h1 className="pagehead__title">GPO Governance</h1>
              <div className="pagehead__facts">
                <span className="fact">Group Policy Object oversight</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="page-loading" role="status" aria-live="polite">
                Loading GPO data…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (gpoError) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Active Directory</p>
              <h1 className="pagehead__title">GPO Governance</h1>
              <div className="pagehead__facts">
                <span className="fact">Group Policy Object oversight</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state state--risk">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Failed to load GPO data</h3>
                <p className="state__body">
                  Could not connect to the Active Directory API. Please check your connection and try again.
                </p>
                <button className="btn btn--primary" onClick={() => refetchGpo()}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Active Directory</p>
            <h1 className="pagehead__title">GPO Governance</h1>
            <div className="pagehead__facts">
              <span className="fact">Group Policy Object oversight — linkage, STIG compliance, and group hygiene</span>
              <span className="fact">{gpos.length} GPOs tracked</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {/* Completeness statement */}
        <div className="notice notice--info">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div>
            <p className="notice__title">GPO Coverage</p>
            <p className="notice__body">
              ATLAS tracks <strong>{gpos.length} GPOs</strong> from Active Directory.
              {orphanLinked.length > 0 && (
                <> <strong>{orphanLinked.length}</strong> linked to no OU.</>
              )}
              {drift.length > 0 && (
                <> <strong>{nonCompliantCount}</strong> STIG rules non-compliant.</>
              )}
            </p>
          </div>
        </div>

        {/* Orphan Detection — Indicator Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="Orphan detection summary">
          <div className="ind ind--watch">
            <div className="ind__head">
              <p className="ind__abbr">Unlinked</p>
              <p className="ind__full">GPOs not linked to any OU</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{orphanLinked.length}</p>
            </div>
            <p className="ind__reading">
              <i style={{ background: 'var(--gold)' }}></i>
              <b style={{ color: 'var(--gold)' }}>Monitor</b> — linked to no organizational unit
            </p>
          </div>

          <div className="ind ind--watch">
            <div className="ind__head">
              <p className="ind__abbr">Stale</p>
              <p className="ind__full">Unmodified over 2 years</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{orphanStale.length}</p>
            </div>
            <p className="ind__reading">
              <i style={{ background: 'var(--gold)' }}></i>
              <b style={{ color: 'var(--gold)' }}>Monitor</b> — frozen policy, likely abandoned
            </p>
          </div>

          <div className="ind ind--breach">
            <div className="ind__head">
              <p className="ind__abbr">Zero-Principal</p>
              <p className="ind__full">Security filters with no applicable principal</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{orphanZeroFilter.length}</p>
            </div>
            <p className="ind__reading">
              <i></i>
              <b>Action required</b> — security filter resolves to no accounts
            </p>
          </div>
        </div>

        {/* GPO Inventory Table */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Inventory</p>
              <h2 className="box__title">GPO Inventory</h2>
            </div>
            <div className="box__head-actions">
              <span className="fact">{gpos.length} objects</span>
            </div>
          </div>
          <div className="box__body box__body--flush">
            {gpos.length > 0 ? (
              <DataTable
                columns={gpoColumns}
                visibleColumns={gpoCtrl.visibleColumns}
                data={gpoCtrl.paged}
                search={gpoCtrl.search}
                onSearchChange={gpoCtrl.setSearch}
                sortKey={gpoCtrl.sortKey}
                sortDir={gpoCtrl.sortDir}
                onSort={gpoCtrl.setSort}
                page={gpoCtrl.page}
                totalPages={gpoCtrl.totalPages}
                totalFiltered={gpoCtrl.totalFiltered}
                pageSize={gpoCtrl.pageSize}
                onPageChange={gpoCtrl.setPage}
                onPageSizeChange={gpoCtrl.setPageSize}
                activeFilters={gpoCtrl.activeFilters}
                onFilter={gpoCtrl.setFilter}
                onClearFilters={gpoCtrl.clearFilters}
                timeRange={gpoCtrl.timeRange}
                onTimeRangeChange={gpoCtrl.setTimeRange}
                hiddenColumns={gpoCtrl.hiddenColumns}
                onToggleColumn={gpoCtrl.toggleColumn}
                selectedIds={gpoCtrl.selectedIds}
                onToggleSelect={gpoCtrl.toggleSelect}
                onSelectAll={gpoCtrl.selectAll}
                onClearSelection={gpoCtrl.clearSelection}
                savedViews={gpoCtrl.savedViews}
                onSaveView={gpoCtrl.saveView}
                onLoadView={gpoCtrl.loadView}
                onDeleteView={gpoCtrl.deleteView}
                onExportCSV={gpoCtrl.exportCSV}
                onExportExcel={gpoCtrl.exportExcel}
                searchPlaceholder="Search GPOs..."
                keyAccessor={(r: any) => r.id || r.name}
                emptyMessage={
                  gpoCtrl.search.trim() || Object.keys(gpoCtrl.activeFilters).length > 0
                    ? 'No GPOs match the current search/filter.'
                    : 'No GPOs ingested. Connect an Active Directory source to populate.'
                }
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <h3 className="state__title">No GPOs ingested</h3>
                <p className="state__body">
                  Connect an Active Directory source to populate GPO inventory.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Orphan Detection Table */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Orphans</p>
              <h2 className="box__title">Orphan Detection</h2>
            </div>
          </div>
          <div className="box__body box__body--flush">
            {orphansCombined.length > 0 ? (
              <DataTable
                columns={orphanColumns}
                visibleColumns={orphanCtrl.visibleColumns}
                data={orphanCtrl.paged}
                search={orphanCtrl.search}
                onSearchChange={orphanCtrl.setSearch}
                sortKey={orphanCtrl.sortKey}
                sortDir={orphanCtrl.sortDir}
                onSort={orphanCtrl.setSort}
                page={orphanCtrl.page}
                totalPages={orphanCtrl.totalPages}
                totalFiltered={orphanCtrl.totalFiltered}
                pageSize={orphanCtrl.pageSize}
                onPageChange={orphanCtrl.setPage}
                onPageSizeChange={orphanCtrl.setPageSize}
                activeFilters={orphanCtrl.activeFilters}
                onFilter={orphanCtrl.setFilter}
                onClearFilters={orphanCtrl.clearFilters}
                timeRange={orphanCtrl.timeRange}
                onTimeRangeChange={orphanCtrl.setTimeRange}
                hiddenColumns={orphanCtrl.hiddenColumns}
                onToggleColumn={orphanCtrl.toggleColumn}
                selectedIds={orphanCtrl.selectedIds}
                onToggleSelect={orphanCtrl.toggleSelect}
                onSelectAll={orphanCtrl.selectAll}
                onClearSelection={orphanCtrl.clearSelection}
                savedViews={orphanCtrl.savedViews}
                onSaveView={orphanCtrl.saveView}
                onLoadView={orphanCtrl.loadView}
                onDeleteView={orphanCtrl.deleteView}
                onExportCSV={orphanCtrl.exportCSV}
                onExportExcel={orphanCtrl.exportExcel}
                searchPlaceholder="Search orphans..."
                keyAccessor={(r: any) => `${r.issue}-${r.name}`}
                emptyMessage="No orphan GPOs detected."
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <h3 className="state__title">No orphan GPOs detected</h3>
                <p className="state__body">
                  All GPOs are properly linked and maintained.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* STIG Drift Matrix */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Compliance</p>
              <h2 className="box__title">STIG Drift Matrix</h2>
            </div>
            <div className="box__head-actions">
              <span className="fact">{drift.length} rules evaluated</span>
            </div>
          </div>
          <div className="box__body box__body--flush">
            {drift.length > 0 ? (
              <DataTable
                columns={driftColumns}
                visibleColumns={driftCtrl.visibleColumns}
                data={driftCtrl.paged}
                search={driftCtrl.search}
                onSearchChange={driftCtrl.setSearch}
                sortKey={driftCtrl.sortKey}
                sortDir={driftCtrl.sortDir}
                onSort={driftCtrl.setSort}
                page={driftCtrl.page}
                totalPages={driftCtrl.totalPages}
                totalFiltered={driftCtrl.totalFiltered}
                pageSize={driftCtrl.pageSize}
                onPageChange={driftCtrl.setPage}
                onPageSizeChange={driftCtrl.setPageSize}
                activeFilters={driftCtrl.activeFilters}
                onFilter={driftCtrl.setFilter}
                onClearFilters={driftCtrl.clearFilters}
                timeRange={driftCtrl.timeRange}
                onTimeRangeChange={driftCtrl.setTimeRange}
                hiddenColumns={driftCtrl.hiddenColumns}
                onToggleColumn={driftCtrl.toggleColumn}
                selectedIds={driftCtrl.selectedIds}
                onToggleSelect={driftCtrl.toggleSelect}
                onSelectAll={driftCtrl.selectAll}
                onClearSelection={driftCtrl.clearSelection}
                savedViews={driftCtrl.savedViews}
                onSaveView={driftCtrl.saveView}
                onLoadView={driftCtrl.loadView}
                onDeleteView={driftCtrl.deleteView}
                onExportCSV={driftCtrl.exportCSV}
                onExportExcel={driftCtrl.exportExcel}
                searchPlaceholder="Search STIG rules..."
                keyAccessor={(r: any) => r.rule_id}
                emptyMessage="No STIG drift data. Map GPO settings to DISA STIG rules to enable compliance tracking."
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <h3 className="state__title">No STIG drift data</h3>
                <p className="state__body">
                  Map GPO settings to DISA STIG rules to enable compliance tracking.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Group Hygiene */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Groups</p>
              <h2 className="box__title">Group Hygiene</h2>
            </div>
            <div className="box__head-actions">
              <span className="fact">{hygiene.length} groups evaluated</span>
            </div>
          </div>
          <div className="box__body box__body--flush">
            {hygiene.length > 0 ? (
              <DataTable
                columns={hygieneColumns}
                visibleColumns={hygieneCtrl.visibleColumns}
                data={hygieneCtrl.paged}
                search={hygieneCtrl.search}
                onSearchChange={hygieneCtrl.setSearch}
                sortKey={hygieneCtrl.sortKey}
                sortDir={hygieneCtrl.sortDir}
                onSort={hygieneCtrl.setSort}
                page={hygieneCtrl.page}
                totalPages={hygieneCtrl.totalPages}
                totalFiltered={hygieneCtrl.totalFiltered}
                pageSize={hygieneCtrl.pageSize}
                onPageChange={hygieneCtrl.setPage}
                onPageSizeChange={hygieneCtrl.setPageSize}
                activeFilters={hygieneCtrl.activeFilters}
                onFilter={hygieneCtrl.setFilter}
                onClearFilters={hygieneCtrl.clearFilters}
                timeRange={hygieneCtrl.timeRange}
                onTimeRangeChange={hygieneCtrl.setTimeRange}
                hiddenColumns={hygieneCtrl.hiddenColumns}
                onToggleColumn={hygieneCtrl.toggleColumn}
                selectedIds={hygieneCtrl.selectedIds}
                onToggleSelect={hygieneCtrl.toggleSelect}
                onSelectAll={hygieneCtrl.selectAll}
                onClearSelection={hygieneCtrl.clearSelection}
                savedViews={hygieneCtrl.savedViews}
                onSaveView={hygieneCtrl.saveView}
                onLoadView={hygieneCtrl.loadView}
                onDeleteView={hygieneCtrl.deleteView}
                onExportCSV={hygieneCtrl.exportCSV}
                onExportExcel={hygieneCtrl.exportExcel}
                searchPlaceholder="Search groups..."
                keyAccessor={(r: any) => r.name}
                emptyMessage="No group hygiene data available."
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <h3 className="state__title">No group hygiene data</h3>
                <p className="state__body">
                  Group hygiene data will appear once AD groups are ingested.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
